import { z } from 'zod'
import { BaseCsvExportRequest, baseCsvExportRequestSchema } from './CsvExportTypes'

export const TIMESHEETS_EXPORT_MODES = ['TOTALS', 'VISITS'] as const
export type TimesheetsExportMode = (typeof TIMESHEETS_EXPORT_MODES)[number]

const TECHNICIAN_INFO_FIELD_IDS = ['technicianName', 'technicianRole', 'businessUnitName'] as const
const TOTALS_FIELD_IDS = [
  'totalHours',
  'dailyHours',
  'driveTimeHours',
  'onSiteHours',
  'nonPayableHours',
  'jobsWorked',
] as const
const VISITS_FIELD_IDS = [
  'jobNumber',
  'visitDay',
  'startTime',
  'endTime',
  'duration',
  'activityType',
  'payable',
] as const

export const TIMESHEETS_CSV_EXPORT_FIELD_IDS = [
  ...TECHNICIAN_INFO_FIELD_IDS,
  ...TOTALS_FIELD_IDS,
  ...VISITS_FIELD_IDS,
] as const

export type TimesheetsCsvExportFieldId = (typeof TIMESHEETS_CSV_EXPORT_FIELD_IDS)[number]

export type TimesheetsFieldGroupId = 'TECHNICIAN_INFO' | 'TIME_SUMMARY' | 'VISIT_INFORMATION'

export type TimesheetsCsvExportFieldDefinition = {
  id: TimesheetsCsvExportFieldId
  label: string
  description?: string
  groupId: TimesheetsFieldGroupId
  defaultSelected: boolean
}

export const timesheetsCsvExportFieldDefinitions: Record<
  TimesheetsCsvExportFieldId,
  TimesheetsCsvExportFieldDefinition
> = {
  technicianName: {
    id: 'technicianName',
    label: 'Technician Name',
    groupId: 'TECHNICIAN_INFO',
    defaultSelected: true,
  },
  technicianRole: {
    id: 'technicianRole',
    label: 'Role',
    groupId: 'TECHNICIAN_INFO',
    defaultSelected: false,
  },
  businessUnitName: {
    id: 'businessUnitName',
    label: 'Business Unit',
    groupId: 'TECHNICIAN_INFO',
    defaultSelected: false,
  },
  totalHours: {
    id: 'totalHours',
    label: 'Total Hours',
    groupId: 'TIME_SUMMARY',
    defaultSelected: true,
  },
  dailyHours: {
    id: 'dailyHours',
    label: 'Daily Hours',
    groupId: 'TIME_SUMMARY',
    defaultSelected: true,
  },
  driveTimeHours: {
    id: 'driveTimeHours',
    label: 'Drive Time Hours',
    groupId: 'TIME_SUMMARY',
    defaultSelected: false,
  },
  onSiteHours: {
    id: 'onSiteHours',
    label: 'On-Site Hours',
    groupId: 'TIME_SUMMARY',
    defaultSelected: false,
  },
  nonPayableHours: {
    id: 'nonPayableHours',
    label: 'Non-Payable Hours',
    groupId: 'TIME_SUMMARY',
    defaultSelected: false,
  },
  jobsWorked: {
    id: 'jobsWorked',
    label: 'Jobs Worked',
    groupId: 'TIME_SUMMARY',
    defaultSelected: false,
  },
  jobNumber: {
    id: 'jobNumber',
    label: 'Job Number',
    groupId: 'VISIT_INFORMATION',
    defaultSelected: true,
  },
  visitDay: {
    id: 'visitDay',
    label: 'Day',
    groupId: 'VISIT_INFORMATION',
    defaultSelected: true,
  },
  startTime: {
    id: 'startTime',
    label: 'Start Time',
    groupId: 'VISIT_INFORMATION',
    defaultSelected: true,
  },
  endTime: {
    id: 'endTime',
    label: 'End Time',
    groupId: 'VISIT_INFORMATION',
    defaultSelected: true,
  },
  duration: {
    id: 'duration',
    label: 'Duration',
    groupId: 'VISIT_INFORMATION',
    defaultSelected: true,
  },
  activityType: {
    id: 'activityType',
    label: 'Activity Type',
    groupId: 'VISIT_INFORMATION',
    defaultSelected: true,
  },
  payable: {
    id: 'payable',
    label: 'Payable',
    groupId: 'VISIT_INFORMATION',
    defaultSelected: true,
  },
}

export const timesheetsTotalsFieldGroups: {
  id: TimesheetsFieldGroupId
  label: string
  description?: string
  fieldIds: TimesheetsCsvExportFieldId[]
}[] = [
  {
    id: 'TECHNICIAN_INFO',
    label: 'Technician Information',
    fieldIds: [...TECHNICIAN_INFO_FIELD_IDS],
  },
  {
    id: 'TIME_SUMMARY',
    label: 'Time Summary',
    fieldIds: [...TOTALS_FIELD_IDS],
  },
]

export const timesheetsVisitsFieldGroups: {
  id: TimesheetsFieldGroupId
  label: string
  description?: string
  fieldIds: TimesheetsCsvExportFieldId[]
}[] = [
  {
    id: 'TECHNICIAN_INFO',
    label: 'Technician Information',
    fieldIds: [...TECHNICIAN_INFO_FIELD_IDS],
  },
  {
    id: 'VISIT_INFORMATION',
    label: 'Visit Information',
    fieldIds: [...VISITS_FIELD_IDS],
  },
]

export const timesheetsCsvExportFiltersSchema = z.object({
  mode: z.enum(TIMESHEETS_EXPORT_MODES),
  timeWindow: z.object({
    start: z.string(),
    end: z.string(),
  }),
})

export type TimesheetsCsvExportFilters = z.infer<typeof timesheetsCsvExportFiltersSchema>

export const timesheetsCsvExportRequestSchema = baseCsvExportRequestSchema.extend({
  fieldIds: z.array(z.enum(TIMESHEETS_CSV_EXPORT_FIELD_IDS)),
  filters: timesheetsCsvExportFiltersSchema,
})

export type TimesheetsCsvExportRequest = z.infer<typeof timesheetsCsvExportRequestSchema>

export type TimesheetsTotalsExportRow = {
  technicianName: string
  technicianRole?: string
  businessUnitName?: string
  totalHours: number
  dailyHours: Record<string, number>
  driveTimeHours: number
  onSiteHours: number
  nonPayableHours: number
  jobsWorked: number
}

export type TimesheetsVisitExportRow = {
  technicianName: string
  technicianRole?: string
  businessUnitName?: string
  jobNumber?: string
  visitDay: string
  startTime: string
  endTime: string
  duration: string
  activityType: string
  payable: boolean
}

export type TimesheetsCsvExportRow = TimesheetsTotalsExportRow | TimesheetsVisitExportRow

export type TimesheetsCsvExportBaseRequest = BaseCsvExportRequest
